/**
 * @component LoadingButton
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 * @spec Based on shadcn/ui + Figma design specification
 */

import { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';
import { Loader2 } from 'lucide-react';

export type LoadingButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
export type LoadingButtonSize = 'small' | 'regular' | 'large';

export interface LoadingButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: LoadingButtonVariant;
  size?: LoadingButtonSize;
  loading?: boolean;
  loadingText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const loadingButtonVariants = cva(
  'inline-flex items-center justify-center gap-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-md',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-accent-1 text-foreground hover:bg-accent-2',
        outline: 'border border-border bg-background text-foreground hover:bg-accent-0',
        ghost: 'text-foreground hover:bg-accent-1',
        destructive: 'bg-danger text-danger-foreground hover:bg-danger/90',
      },
      size: {
        small: 'h-8 px-3 text-sm [&>svg]:w-4 [&>svg]:h-4',
        regular: 'h-10 px-4 text-sm [&>svg]:w-4 [&>svg]:h-4',
        large: 'h-12 px-6 text-base [&>svg]:w-5 [&>svg]:h-5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'regular',
    },
  }
);

export const LoadingButton = forwardRef<HTMLButtonElement, LoadingButtonProps>(
  ({ className, variant, size, loading, loadingText, leftIcon, rightIcon, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(loadingButtonVariants({ variant, size, className }))}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" />
            {loadingText || children}
          </>
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </button>
    );
  }
);

LoadingButton.displayName = 'LoadingButton';
